import React, { useEffect, useRef } from "react";
import { Canvas, FabricObject } from "fabric";
import { Box } from "@mui/material";

// Fabric.js v6/v7 changed the default object origins to 'center'. 
// We restore classic 'left' and 'top' behavior globally to match our A4 pixel layouts.
FabricObject.prototype.originX = "left";
FabricObject.prototype.originY = "top";
FabricObject.ownDefaults.originX = "left";
FabricObject.ownDefaults.originY = "top";

interface CertificateCanvasProps {
  onCanvasReady: (canvas: Canvas) => void;
  width?: number;
  height?: number;
}

export default function CertificateCanvas({
  onCanvasReady,
  width = 842, // Default A4 Landscape in pixels at 72 DPI
  height = 595,
}: CertificateCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fabricCanvasRef = useRef<Canvas | null>(null);

  // Use a stable ref for onCanvasReady to prevent re-initializing canvas on prop changes
  const onCanvasReadyRef = useRef(onCanvasReady);
  useEffect(() => {
    onCanvasReadyRef.current = onCanvasReady;
  }, [onCanvasReady]);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Create the Fabric.js Canvas instance using modern v7.4.0 constructor
    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor: "#ffffff",
      preserveObjectStacking: true, // Retains Z-index hierarchy when selecting objects
      enableRetinaScaling: false,   // Prevents high-DPI scaling coordinate mismatches
    });

    fabricCanvasRef.current = canvas;
    onCanvasReadyRef.current(canvas);

    // Clean up resources on unmount
    return () => {
      canvas.dispose().catch((err) => {
        console.error("Failed to dispose fabric canvas:", err);
      });
    };
  }, [width, height]); // Removed onCanvasReady to prevent constant re-initialization

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 10px 30px rgba(17,24,39,0.08)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        display: "inline-block",
      }}
    >
      <canvas ref={canvasRef} />
    </Box>
  );
}
